import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { DiarioObrasLista } from "../../domain/models/diario_obras_lista";
import { useDiarioObrasPagination } from "../hooks/diario_obras/pagination";
import { useDiarioObrasFilters } from "../hooks/diario_obras/filter";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../common/hooks/use_debounced_callback";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useNavigate, useParams } from "react-router";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { boolean } from "zod";

type LoadDiarioObraFuncState = {
  IdDiarioObras?: number;
  page?: number;
  rowsPerPage?: number;
  ResponsavelPesquisa?: number;
  ClientePesquisa?: number;
  FornecedorPesquisa?: number;
  status?: number;
  pesquisa?: string | number | Date;
  centroDeCusto?: number[];
  centroDeCustoLista?: CentroCustoDropDown[] | undefined;
  TipoTempo?: number;
  TipoStatus?: number;
  TipoDiaPraticavel?: number;
  dataReceberDe?: Date | null;
  dataReceberAte?: Date | null;
  pendentes?: boolean | null;
};

export const useDiarioObraListaViewModel = () => {
  const {
    commons: {},
    reports: { getReportDiarioObraIndividualUseCase },
    diarioObra: {
      getAllListagemDiarioObrasUseCase,
      deleteDiarioObraUseCase,
      copiaDiarioObraUseCase,
      aprovarDiarioObraUseCase,
    },
  } = useDependencyInjection();
  const debouncedRef = useCreateDebounceHandler(750);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [diarioObrasLista, setDiarioObrasLista] = useState<DiarioObrasLista[]>(
    [],
  );
  const [selectedDiarioObra, setSelectedDiarioObra] =
    useState<DiarioObrasLista>();
  const [diarioObraDeleteModal, setDiarioObraDeleteModal] =
    useState<boolean>(false);
  const [openCopiaModal, setOpenCopiaModal] = useState<boolean>(false);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>();
  const {
    pushErrorNotification,
    setCustomLoading,
    pushInfoNotification,
    pushWarnNotification,
  } = usePageLoader();
  const filters = useDiarioObrasFilters();
  const pagination = useDiarioObrasPagination();
  const navigate = useNavigate();
  const { idUsuario } = useUserAuthStore();
  const { d, pendentes } = useParams();

  const { loadCentroCustoDropDown } = useDropDownLoader();

  //#region Relatorio
  const [reportViewerState, startReportViewerProcess] = useReportViewer();
  //#endregion

  // #region Funções de carregamento

  const loadDiarioObrasListaState = useCallback(
    async (funcState?: LoadDiarioObraFuncState) => {
      try {
        const filters = useDiarioObrasFilters.getState();
        const pagination = useDiarioObrasPagination.getState();
        if (d !== undefined && d !== null)
          filters.TipoDiaPraticavel = Number(d);
        if (pendentes !== undefined && pendentes !== null && funcState?.pendentes != false){
          filters.SomentePendentes = pendentes === "true";
        }

        const filterID = evaluateParamValue(
          funcState?.IdDiarioObras,
          filters.IdDiarioObras,
        );
        const responsavel =
          funcState?.ResponsavelPesquisa ?? filters.ResponsavelPesquisa;
        const fornecedor =
          funcState?.FornecedorPesquisa ?? filters.FornecedorPesquisa;
        const cliente = funcState?.ClientePesquisa ?? filters.ClientePesquisa;
        const centroDeCusto = funcState?.centroDeCusto ?? filters.centroDeCusto;
        const tipoTempo = funcState?.TipoTempo ?? filters.TipoTempo;
        const tipoStatus = funcState?.TipoStatus ?? filters.TipoStatus;
        const tipoDiaPraticavel =
          funcState?.TipoDiaPraticavel ?? filters.TipoDiaPraticavel;
        const somentePendentes = filters.SomentePendentes;
        const dataReceberDe = evaluateParamValue(
          funcState?.dataReceberDe,
          filters.dataReceberDe,
        );
        const dataReceberAte = evaluateParamValue(
          funcState?.dataReceberAte,
          filters.dataReceberAte,
        );
        const page = funcState?.page ?? pagination.page;
        const rows = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllListagemDiarioObrasUseCase.getAllListagemDiariosObras(
          {
            IdDiarioObras: filterID,
            ResponsavelPesquisa: responsavel,
            FornecedorPesquisa: fornecedor,
            ClientePesquisa: cliente,
            CentroDeCusto: centroDeCusto,
            TipoTempo: tipoTempo,
            TipoStatus: tipoStatus,
            TipoDiaPraticavel: tipoDiaPraticavel,
            DataReceberAte: dataReceberAte,
            DataReceberDe: dataReceberDe,
            SomentePendentes:
              somentePendentes !== undefined ? !!somentePendentes : undefined,
          },
          page,
          rows,
        );
        setDiarioObrasLista(getAllListagemDiarioObrasUseCase.items);
        pagination.setRowsPerPage(getAllListagemDiarioObrasUseCase.pageSize);
        pagination.setTotalItems(getAllListagemDiarioObrasUseCase.totalItems);
      } catch (e: unknown) {
        throw new Error("Falha ao buscar Diario de Obras!");
      }
    },
    [d, getAllListagemDiarioObrasUseCase, pendentes],
  );

  //#endregion

  // #region useEffect

  usePageStartBootstrap(async () => {
    try {
      await loadDiarioObrasListaState();
      await loadCentroCustoDropDown((list) =>
        useDiarioObrasFilters.setState({ centroDeCustoLista: list }),
      );
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadDiarioObrasListaState();
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
        { isAutoFinish: true },
      );
    } finally {
      setLoading(false);
    }
  }, [loadDiarioObrasListaState, pushErrorNotification]);
  //#endregion

  // #region Eventos
  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        pagination.setPage(page + 1);
        await loadDiarioObrasListaState({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [loadDiarioObrasListaState, pagination, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);
        await loadDiarioObrasListaState({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [loadDiarioObrasListaState, pagination, pushErrorNotification],
  );

  //#endregion

  //#region Filtros
  const handleDataReceberRangeChange = useDebouncedCallback(
    debouncedRef,
    (dataReceberDe: Date | null, dataReceberAte: Date | null) => {
      filters.setFilters({ dataReceberDe });
      filters.setFilters({ dataReceberAte });

      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadDiarioObrasListaState({
            dataReceberDe,
            dataReceberAte,
            page: 1,
          });
        } catch (e: unknown) {
          pushErrorNotification(
            "Error",
            processError(e, "Falha ao buscar contas a receber!"),
            { isAutoFinish: true },
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [filters],
  );

  const handleTipoDiaPraticavelChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number) => {
      useDiarioObrasFilters.getState().setTipoDiaPraticavel(newValue);
      return async () => {
        try {
          setLoading(true);
          useDiarioObrasPagination.getState().setPage(1);
          await loadDiarioObrasListaState({
            TipoDiaPraticavel: newValue,
            page: 1,
          });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao carregar Diario de Obras!"),
            { isAutoFinish: true },
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadDiarioObrasListaState, pushErrorNotification],
  );

  const handleTipoTempoChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number) => {
      useDiarioObrasFilters.getState().setTipoTempo(newValue);
      return async () => {
        try {
          setLoading(true);
          useDiarioObrasPagination.getState().setPage(1);
          await loadDiarioObrasListaState({ TipoTempo: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao carregar Diario de Obras!"),
            { isAutoFinish: true },
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadDiarioObrasListaState, pushErrorNotification],
  );
  const handleTipoStatusChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number) => {
      useDiarioObrasFilters.getState().setTipoStatus(newValue);
      return async () => {
        try {
          setLoading(true);
          useDiarioObrasPagination.getState().setPage(1);
          await loadDiarioObrasListaState({ TipoStatus: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao carregar Diario de Obras!"),
            { isAutoFinish: true },
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadDiarioObrasListaState, pushErrorNotification],
  );

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      navigate(`/DiarioObra_lista/null/false`);
      filters.clearFilters();
      pagination.setPage(1);
      await loadDiarioObrasListaState({pendentes: false});
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar Diário de Obras!"),
        { isAutoFinish: true },
      );
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    loadDiarioObrasListaState,
    navigate,
    pagination,
    pushErrorNotification,
  ]);

  const handleCentroDeCustoChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number[]) => {
      useDiarioObrasFilters.getState().setCentroDeCusto(newValue);
      return async () => {
        try {
          setLoading(true);
          useDiarioObrasPagination.getState().setPage(1);
          await loadDiarioObrasListaState({ centroDeCusto: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao carregar Ações Preventivas!"),
            { isAutoFinish: true },
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadDiarioObrasListaState, pushErrorNotification],
  );

  const handleResponsavelChange = useDebouncedCallback(
    debouncedRef,
    async (newValue: number) => {
      useDiarioObrasFilters.getState().setResponsavelPesquisa(newValue);
      try {
        setLoading(true);
        useDiarioObrasPagination.getState().setPage(1);
        await loadDiarioObrasListaState({
          ResponsavelPesquisa: newValue,
          page: 1,
        } as any);
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao carregar Diario de Obras!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
      return () => {};
    },
    [pushErrorNotification, loadDiarioObrasListaState],
  );

  const handleFornecedorChange = useDebouncedCallback(
    debouncedRef,
    async (newValue: number) => {
      useDiarioObrasFilters.getState().setFornecedorPesquisa(newValue);

      try {
        setLoading(true);
        useDiarioObrasPagination.getState().setPage(1);
        await loadDiarioObrasListaState({
          FornecedorPesquisa: newValue,
          page: 1,
        } as any);
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao carregar Diario de Obras!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }

      return () => {};
    },
    [pushErrorNotification, loadDiarioObrasListaState],
  );

  const handleClienteChange = useDebouncedCallback(
    debouncedRef,
    async (newValue: number) => {
      useDiarioObrasFilters.getState().setClientePesquisa(newValue);
      try {
        setLoading(true);
        useDiarioObrasPagination.getState().setPage(1);
        await loadDiarioObrasListaState({
          ClientePesquisa: newValue,
          page: 1,
        } as any);
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao carregar Diario de Obras!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }

      return () => {};
    },
    [pushErrorNotification, loadDiarioObrasListaState],
  );

  //#endregion

  //#region Funções de deletar
  const handleDeleteDiarioModalOpenState = useCallback(
    (IdDiarioObras: number | null, openState: boolean) => {
      if (openState) {
        if (IdDiarioObras && diarioObrasLista) {
          setSelectedDiarioObra(
            diarioObrasLista.find((x) => x.IdDiarioObras === IdDiarioObras),
          );
        } else {
          setSelectedDiarioObra(undefined);
        }
      }
      setDiarioObraDeleteModal(openState);
    },
    [diarioObrasLista],
  );

  const handleDeleteConfirmaDiario = useCallback(async () => {
    if (selectedDiarioObra) {
      try {
        setSaving(true);
        await deleteDiarioObraUseCase.deleteDiarioObra(
          selectedDiarioObra.IdDiarioObras,
        );
        await loadDiarioObrasListaState();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
          { isAutoFinish: true },
        );
      } finally {
        setSaving(false);
        setDiarioObraDeleteModal(false);
      }
    }
  }, [
    selectedDiarioObra,
    deleteDiarioObraUseCase,
    loadDiarioObrasListaState,
    pushErrorNotification,
  ]);

  const handleCadastroDiarioOpenState = useCallback(
    (IdDiarioObra: number | null, openState: boolean) => {
      if (openState) {
        if (IdDiarioObra) {
          navigate(`/DiarioObra_cadastro/${IdDiarioObra}`);
        }
      }
    },
    [navigate],
  );
  //#endregion

  //#region Copia

  const handleCopiaOpenState = useCallback(
    (IdDiarioObras: number | null, openState: boolean) => {
      if (openState) {
        setSelectedDiarioObra(
          diarioObrasLista.find((x) => x.IdDiarioObras === IdDiarioObras),
        );
        setOpenCopiaModal(true);
      } else {
        setOpenCopiaModal(false);
      }
    },
    [diarioObrasLista],
  );

  const handleSubmitCopia = useCallback(async () => {
    try {
      if (selectedDiarioObra && selectedDiarioObra.IdDiarioObras) {
        setSaving(true);
        await copiaDiarioObraUseCase.copiaDiarioObra(
          selectedDiarioObra.IdDiarioObras,
        );

        await loadDiarioObrasListaState();
        pushInfoNotification("Sucesso", "Diario Obras copiada com sucesso!", {
          isAutoFinish: true,
        });

        setOpenCopiaModal(false);
      }
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao copiar Copiar Diario!"),
        { isAutoFinish: true },
      );
    } finally {
      setSaving(false);
    }
  }, [
    selectedDiarioObra,
    copiaDiarioObraUseCase,
    loadDiarioObrasListaState,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  //#endregion

  //#region Aprovar
  const handleAlterarInativoAtivoSubmit = useCallback(async () => {
    try {
      setSaving(true);
      if (rowSelectionModel) {
        const diariosObrasIds: number[] = [];
        for (let row of rowSelectionModel.ids.values()) {
          if (typeof row === "number") {
            diariosObrasIds.push(row);
          } else if (typeof row === "string") {
            try {
              diariosObrasIds.push(Number(row));
            } catch (e) {}
          }
        }
        await aprovarDiarioObraUseCase.aprovarDiarioObra(
          idUsuario,
          diariosObrasIds ?? [],
        );

        await loadDiarioObrasListaState();

        pushInfoNotification(
          "Sucesso",
          "Diarios de Obras atualizados com sucesso!",
          { isAutoFinish: true },
        );
      }
    } catch (e: any) {
      pushErrorNotification(
        "Erro",
        "Não é possível realizar a ação, o usuário não está vinculado como funcionário!",
        { isAutoFinish: true },
      );
    } finally {
      setSaving(false);
      setRowSelectionModel({ type: "include", ids: new Set() });
    }
  }, [
    rowSelectionModel,
    aprovarDiarioObraUseCase,
    idUsuario,
    pushInfoNotification,
    loadDiarioObrasListaState,
    pushErrorNotification,
  ]);

  const handleAprovaModal = useCallback(() => {
    if (!rowSelectionModel || rowSelectionModel.ids.size === 0) {
      pushWarnNotification(
        "Atenção",
        "Selecione os diarios a serem alterados!",
      );
      return;
    }
    handleAlterarInativoAtivoSubmit();
  }, [
    handleAlterarInativoAtivoSubmit,
    pushWarnNotification,
    rowSelectionModel,
  ]);

  const handleRowSelectionModelChange = useCallback(
    (rows: GridRowSelectionModel) => {
      setRowSelectionModel(rows);
    },
    [],
  );

  //#endregion

  //#region  relatorio
  const handleReportDiarioObraIndividual = useCallback(
    async (id: number) => {
      try {
        setLoading(true);

        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportDiarioObraIndividualUseCase.getReportDiarioObraIndividual(
              requiredMimeType,
              id,
            ),
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(
            e,
            "Falha ao gerar relatorio de Diario Obra Individual!",
          ),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      getReportDiarioObraIndividualUseCase,
      pushErrorNotification,
      startReportViewerProcess,
    ],
  );

  //#endregion

  return {
    loading,
    errorMessage,
    saving,
    pagination,
    filters,
    diarioObrasLista,
    diarioObraDeleteModal,
    openCopiaModal,
    reportViewerState,
    handlePageChange,
    handleRowsPerPageChange,
    handleReload,
    handleClearFilters,
    handleResponsavelChange,
    handleCentroDeCustoChange,
    handleFornecedorChange,
    handleClienteChange,
    handleTipoStatusChange,
    handleTipoTempoChange,
    handleTipoDiaPraticavelChange,
    handleDataReceberRangeChange,
    handleDeleteConfirmaDiario,
    handleDeleteDiarioModalOpenState,
    handleCadastroDiarioOpenState,
    handleSubmitCopia,
    handleCopiaOpenState,
    handleRowSelectionModelChange,
    handleAprovaModal,
    handleAlterarInativoAtivoSubmit,
    handleReportDiarioObraIndividual,
  };
};
